import React, { useCallback, useEffect, useState } from 'react';

import { getIframeSrc, getNameExtension, isImg } from '@shared/helpers';
import { makeDecodeURL } from '@shared/helpers/makeDecodeURL';
import { lightboxSliceActions } from '@shared/ui/Lightbox/model/lightboxSlice';
import { LightboxFile } from '@shared/ui/Lightbox/ui/Lightbox';

import { useAppDispatch } from '../../../../../store';

export const useLightbox = (currentFileId: number, files: LightboxFile[], onClose: () => void) => {
    const foundIndex = files.findIndex((file) => file.id === currentFileId);
    const dispatch = useAppDispatch();
    const [currentIndex, setCurrentIndex] = useState(foundIndex);

    useEffect(() => {
        setCurrentIndex(foundIndex === -1 ? 0 : foundIndex);
    }, [foundIndex]);

    const prev = useCallback(() => {
        const newIndex = Math.max(0, currentIndex - 1);
        setCurrentIndex(newIndex);
        dispatch(lightboxSliceActions.onChangeFile(files[newIndex].id));
    }, [currentIndex, dispatch, files]);

    const next = useCallback(() => {
        const newIndex = Math.min(files.length - 1, currentIndex + 1);
        setCurrentIndex(newIndex);
        dispatch(lightboxSliceActions.onChangeFile(files[newIndex].id));
    }, [files.length, currentIndex, dispatch, files]);

    const showPrev = (e: React.MouseEvent) => {
        e.stopPropagation();
        prev();
    };

    const showNext = (e: React.MouseEvent) => {
        e.stopPropagation();
        next();
    };

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                onClose?.();
            }
            if (event.key === 'ArrowLeft') {
                prev();
            }
            if (event.key === 'ArrowRight') {
                next();
            }
        };

        window.addEventListener('keydown', handleKeyDown, true);

        return () => {
            window.removeEventListener('keydown', handleKeyDown, true);
        };
    }, [onClose, next, prev]);

    const currentFile = files[currentIndex];

    const { extension } = getNameExtension(currentFile?.fileName || '');
    const { extension: extensionFromSrc } = getNameExtension(currentFile?.fileSrc || '');

    const decodeURL = makeDecodeURL(currentFile?.fileSrc || '');

    const iframeSrc = getIframeSrc(extension) ?? getIframeSrc(extensionFromSrc);
    const isFileImg = isImg(currentFile?.mimeType || '');

    const isPrevFormat = iframeSrc !== null || !!isFileImg;

    // console.log('iframeSrc', iframeSrc);
    // console.log('currentFile', currentFile);
    // console.log('isPrevFormat', isPrevFormat);
    // console.log('isFileImg', isFileImg);
    // console.log('extension', extension);

    return {
        showPrev,
        showNext,
        decodeURL,
        iframeSrc,
        isFileImg,
        currentIndex,
        currentFile,
        isPrevFormat,
    };
};
